// Auth + onboarding gate — wraps AdminLayout.
// 1. Verifies auth (redirects to /sign-in if needed)
// 2. Loads teams
// 3. If no teams → NewRestaurantSetup wizard
// 4. If multiple teams and none selected → team picker
// 5. If teams loaded + team selected → render app

import { useEffect, useCallback, useRef, useState } from 'react'
import * as keycast from '@/lib/keycastClient'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '@/state/useAuth'
import { BASIC_LANDING_PATH } from '@/shell/navRegistry'
import { useTeam } from '@/state/useTeam'
import { readSavedActiveTeamId } from '@/lib/savedTeam'
import { useAdminStatus } from '@/state/useAdminStatus'
import { NewRestaurantSetup } from '@/components/NewRestaurantSetup'
import { SupportPicker } from '@/components/SupportPicker'

export function AuthGate() {
  const { status, initialize } = useAuth()
  const {
    teams, activeTeamId, loading: teamsLoading,
    loadTeams, selectTeam, addSupportSession,
  } = useTeam()
  const { role: adminRole, loaded: adminLoaded, refresh: refreshAdminStatus } = useAdminStatus()
  const navigate = useNavigate()

  const [teamsLoadAttempted, setTeamsLoadAttempted] = useState(false)
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false)
  const [showSetup, setShowSetup] = useState(false)
  const [restoreFailed, setRestoreFailed] = useState(false)
  const restoreStarted = useRef(false)

  // Step 1: Initialize auth
  useEffect(() => {
    if (status === 'idle') initialize()
  }, [status, initialize])

  // Step 2: Load teams + probe admin status after auth is ready
  useEffect(() => {
    if (status === 'ready' && !teamsLoadAttempted && !teamsLoading) {
      setTeamsLoadAttempted(true)
      loadTeams()
      refreshAdminStatus()
    }
  }, [status, teamsLoadAttempted, teamsLoading, loadTeams, refreshAdminStatus])

  // Step 3: Auto-select only when the user has exactly one team.
  // Multi-team users go through TeamPicker; admin/support users go
  // through SupportPicker.
  useEffect(() => {
    if (!teamsLoadAttempted || teamsLoading || !adminLoaded) return
    if (autoSelectAttempted) return
    if (activeTeamId) return
    if (adminRole !== null) {
      setAutoSelectAttempted(true)
      return
    }

    setAutoSelectAttempted(true)
    if (teams.length === 1) {
      selectTeam(teams[0].id)
    }
  }, [teamsLoadAttempted, teamsLoading, adminLoaded, adminRole, autoSelectAttempted, activeTeamId, teams, selectTeam])

  // Step 3b: A support or admin agent who reloads the page (or opens a link) has no live selection, so
  // the picker would show. Reopen the restaurant the agent last worked in instead, by the same grant the
  // picker uses. If that fails, the picker stays.
  const savedTeamId = readSavedActiveTeamId()
  const restoreTeam =
    adminRole !== null && !activeTeamId && !restoreFailed && savedTeamId !== null
      ? teams.find((t) => t.id === savedTeamId)
      : undefined
  const restoreId = restoreTeam?.id
  useEffect(() => {
    if (!teamsLoadAttempted || teamsLoading || !adminLoaded || restoreId === undefined) return
    if (restoreStarted.current) return
    restoreStarted.current = true
    void (async () => {
      try {
        const res = await keycast.grantSupportAccess(restoreId)
        await addSupportSession({
          teamId: restoreId,
          teamName: restoreTeam?.name ?? 'Restaurant',
          storedKeyPubkey: res.stored_key_pubkey,
          bunkerUrl: res.bunker_url,
        })
      } catch {
        setRestoreFailed(true)
      }
    })()
  }, [teamsLoadAttempted, teamsLoading, adminLoaded, restoreId, restoreTeam?.name, addSupportSession])

  // Step 4: After teams load, decide if setup is needed
  useEffect(() => {
    if (teamsLoadAttempted && !teamsLoading) {
      if (teams.length === 0 && !activeTeamId) {
        setShowSetup(true)
      } else {
        setShowSetup(false)
      }
    }
  }, [teamsLoadAttempted, teamsLoading, teams.length, activeTeamId])

  // After setup completes, land on the Profile — every plan. The wizard's
  // Google candidate is waiting there to be saved, and a restaurant with no
  // profile has nothing for Pulse to show yet.
  // Teams are already loaded and the new team is already activated
  // by startProvisioning (loadTeams + activateTeam). Do NOT reset
  // teamsLoadAttempted — that triggers a re-load cycle that races
  // with the Zustand activeTeamId, causing support users to land
  // on SupportPicker instead of the app.
  const handleSetupComplete = useCallback(() => {
    setShowSetup(false)
    navigate(BASIC_LANDING_PATH)
  }, [navigate])

  // Auth loading
  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  // Not authenticated
  if (status === 'needs-setup' || status === 'error') {
    return <Navigate to="/sign-in" replace />
  }

  // Teams + admin status loading (first attempt)
  if (!teamsLoadAttempted || teamsLoading || !adminLoaded) {
    return (
      <div className="flex h-screen items-center justify-center" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  // The last restaurant is being reopened: show the spinner, not the picker.
  if (restoreTeam) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Reopening {restoreTeam.name}…</p>
      </div>
    )
  }

  // Admin/support user with no team selected → SupportPicker
  if (adminRole !== null && !activeTeamId) {
    return <SupportPicker />
  }

  // No teams → new restaurant setup
  if (showSetup) {
    return <NewRestaurantSetup onComplete={handleSetupComplete} />
  }

  // Multiple teams, none selected → team picker
  if (teams.length > 1 && !activeTeamId) {
    return <TeamPicker />
  }

  // Teams exist + team selected — render app. Bunker connection happens async;
  // BrandHeader shows OFFLINE until bunker connects.
  return <Outlet />
}

// ---------- Full-screen team picker ----------

function TeamPicker() {
  const { teams, selectTeam } = useTeam()
  const [selecting, setSelecting] = useState<number | null>(null)

  async function handlePick(tid: number) {
    setSelecting(tid)
    await selectTeam(tid)
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      <h1 className="mb-2 text-xl font-bold text-foreground">Select a restaurant</h1>
      <p className="mb-8 text-sm text-muted-foreground">
        Choose which restaurant you'd like to manage.
      </p>
      <div className="w-full max-w-sm space-y-2">
        {teams.map((team) => {
          const tid = team.id
          const name = team.name
          const isSelecting = selecting === tid
          return (
            <button
              key={tid}
              onClick={() => handlePick(tid)}
              disabled={selecting !== null}
              className="flex w-full items-center rounded-lg border border-border bg-card px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
            >
              {isSelecting ? (
                <div className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                <span className="mr-3 h-4 w-4" />
              )}
              <span className="truncate">{name}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
